// Pure layout helper for the playoff bracket: groups node ids into per-round
// columns and titles each column relative to the final. Shared by the admin
// `BracketView` and the public, read-only `PublicBracket` so both render the
// same column structure. Framework-free and unit-tested.

import { parseNodeId } from '../domain';

/** An i18n key + optional params for a column title (resolved by `t`). */
export interface BracketColumnTitle {
  key: string;
  params?: { count: number };
}

export interface BracketColumn {
  round: number;
  /** English title — used by the admin view and tests. */
  title: string;
  /** Translation key for the same title — used by the public, localised view. */
  titleKey: string;
  titleParams?: { count: number };
  ids: string[];
}

/** Title a round relative to the final (round 0 is the play-in pre-round). */
export function bracketColumnTitle(round: number, maxRound: number): string {
  if (round === 0) return 'Play-in';
  const fromFinal = maxRound - round;
  if (fromFinal === 0) return 'Final';
  if (fromFinal === 1) return 'Semifinals';
  if (fromFinal === 2) return 'Quarterfinals';
  return `Round of ${2 ** (fromFinal + 1)}`;
}

/**
 * The same titling as `bracketColumnTitle`, but as an i18n key + params so a
 * localised view can translate it. Kept in lockstep with the English variant.
 */
export function bracketColumnTitleKey(
  round: number,
  maxRound: number,
): BracketColumnTitle {
  if (round === 0) return { key: 'bracket.playIn' };
  const fromFinal = maxRound - round;
  if (fromFinal === 0) return { key: 'bracket.final' };
  if (fromFinal === 1) return { key: 'bracket.semifinals' };
  if (fromFinal === 2) return { key: 'bracket.quarterfinals' };
  return { key: 'bracket.roundOf', params: { count: 2 ** (fromFinal + 1) } };
}

/**
 * Bucket `nodeOrder` into columns by round (ascending), each with a display
 * title. Node ids that fail to parse fall back to round 0 (the play-in column).
 */
export function buildBracketColumns(
  nodeOrder: readonly string[],
): BracketColumn[] {
  const byRound = new Map<number, string[]>();
  for (const id of nodeOrder) {
    const round = parseNodeId(id)?.round ?? 0;
    const bucket = byRound.get(round);
    if (bucket) bucket.push(id);
    else byRound.set(round, [id]);
  }
  const rounds = [...byRound.keys()].sort((a, b) => a - b);
  const positive = rounds.filter((r) => r > 0);
  const maxRound = positive.length ? Math.max(...positive) : 1;
  return rounds.map((round) => {
    const titleKey = bracketColumnTitleKey(round, maxRound);
    return {
      round,
      title: bracketColumnTitle(round, maxRound),
      titleKey: titleKey.key,
      titleParams: titleKey.params,
      ids: byRound.get(round) ?? [],
    };
  });
}
